import classNames from 'classnames';
import { type CSSProperties, useRef } from 'react';

import { useGaChartPreviewMeta } from '../../../../global-ai-assistant/ga-chart-preview-meta-context';
import { GlobalAiChartPreviewDataReporter } from '../../../../global-ai-assistant/global-ai-assistant-chart-preview-data-reporter';
import { GLOBAL_AI_INLINE_CHART_PLOT_HEIGHT } from '../../../constants/panelWidths';
import { useAdaptiveChartType } from '../../../hooks/useAdaptiveChartType';
import { usePlotMetric } from '../../../hooks/usePlotMetric';
import { MultiMetricDataProvider } from '../../DataProvider/DataProvider';
import {
  usePageConfig,
  useTabContentConfig,
} from '../../PageConfigProvider/PageConfigProvider';
import { RevelioPageId } from '../../PageConfigProvider/types';
import { isCompositionSecondaryDimension } from '../../PageConfigProvider/types/dimension';
import {
  isDynamicDivergingBarComboPlot,
  isShareOfSegmentPlot,
} from '../../PageConfigProvider/types/item';
import { usePageState } from '../../PageState/PageStateProvider';
import { isSingleEntityChart } from '../../PageState/utils/is-single-entity-chart';
import { AiSummaryChart } from '../AiSummary/AiSummaryChart';
import {
  ChartActionMenu,
  ChartHeader,
  ChartHeaderAction,
  ChartHeaderTitleInfo,
  ChartSwitchers,
} from '../ChartHeader';
import { ChartHeaderBreadcrumbProvider } from '../ChartHeaderBreadcrumb';
import { ChartNotSupported } from '../ChartNotSupported';
import { useChart } from '../ChartProvider';
import { DynamicDivergingBarCombo } from '../diverging-bar/DynamicDivergingBarCombo';
import { EntitySummary } from '../EntitySummary/EntitySummary';
import { isChartLoadingFromSwitcher } from '../hooks/useChartSwitcherPlot';
import { IcicleChart } from '../Icicle/IcicleChart';
import { SubsidiariesIcicle } from '../Icicle/SubsidiariesIcicle';
import { MapChartSelector } from '../Map/MapChartSelector';
import { MixedStatsChart } from '../MixedStats/MixedStats';
import { SentimentReview } from '../SentimentReview';
import { ShareOfSegmentChart } from '../ShareOfSegment/ShareOfSegment';
import { SubsidiarySnapshot } from '../SubsidiarySnapshot/SubsidiarySnapshot';
import { SubsidiaryTrend } from '../SubsidiaryTrend/SubsidiaryTrend';
import { TextChartCardContentWithFilters } from '../TextChart/TextChartCardContent';
import { SubsidiariesTimeline } from '../Timeline/SubsidiariesTimeline';
import { ChartCard, type ChartProps } from './Chart';
import cardStyles from './Chart.module.css';
import styles from './special-chart.module.css';

type SpecialChartProps = Pick<
  ChartProps,
  'editMode' | 'item' | 'data' | 'headerSize' | 'primaryFilter' | 'onClick'
> & {
  chartTitle: string;
  metric?: ReturnType<typeof usePlotMetric>;
};

export const SpecialChart = ({
  editMode,
  item,
  data,
  headerSize,
  primaryFilter,
  chartTitle,
  metric,
  onClick,
}: SpecialChartProps) => {
  const printRef = useRef<HTMLDivElement>(null);
  const { pageState } = usePageState();
  const gaChartPreviewMeta = useGaChartPreviewMeta();
  const isGlobalAiChartPreview = gaChartPreviewMeta !== null;
  const loaderContainedInChart =
    editMode ||
    isGlobalAiChartPreview ||
    isChartLoadingFromSwitcher(item.id, pageState.chartSwitcherPlotId);
  const chartType = item?.plot?.chartType;
  const isMapChart = chartType === 'map';
  const hideHeader = item?.hideHeader === true && !isGlobalAiChartPreview;
  const isGlobalAiTextPreview = isGlobalAiChartPreview && chartType === 'text';
  const gaNonMapInlinePlotHeight = GLOBAL_AI_INLINE_CHART_PLOT_HEIGHT;
  const showActionMenu =
    Boolean(
      chartType &&
        (metric || chartType === 'text') &&
        chartType !== 'sentiment_review'
    ) && !isGlobalAiChartPreview;

  const { config: pageConfig } = usePageConfig();
  const isDiscoveryPage =
    pageConfig.revelioPageId === RevelioPageId.TALENT_DISCOVERY ||
    pageConfig.revelioPageId === RevelioPageId.POSTING_DISCOVERY;

  const cardBodyPadding = (() => {
    if (!isMapChart) return undefined;

    if (isDiscoveryPage) return 'discovery-map';

    return 'map';
  })();

  const showPrimaryEntitySwitcher = isSingleEntityChart(item);
  const cardBodyHeight = (() => {
    if (isGlobalAiChartPreview && !isMapChart) {
      return 'auto';
    }
    if (isGlobalAiChartPreview && isMapChart) {
      return 'plot';
    }
    if (isMapChart || hideHeader) {
      return 'full';
    }
    return undefined;
  })();
  const cardBodyMinContent =
    isGlobalAiChartPreview && chartType !== 'text' && !isMapChart;

  const chartBodyContent = (() => {
    if (
      isGlobalAiChartPreview &&
      chartType !== undefined &&
      chartType !== 'text' &&
      !isMapChart
    ) {
      return (
        <div
          className={styles.plotArea}
          style={{ '--plot-height': gaNonMapInlinePlotHeight } as CSSProperties}
        >
          <SpecialChartSelector data={data} />
        </div>
      );
    }
    if (isGlobalAiChartPreview && isMapChart) {
      return (
        <div
          className={styles.mapPlotArea}
          style={
            {
              '--plot-height': GLOBAL_AI_INLINE_CHART_PLOT_HEIGHT,
            } as CSSProperties
          }
        >
          <SpecialChartSelector data={data} />
        </div>
      );
    }
    return <SpecialChartSelector data={data} />;
  })();

  if (isGlobalAiTextPreview) {
    return chartBodyContent;
  }

  return (
    <ChartHeaderBreadcrumbProvider>
      <ChartCard
        onClick={onClick}
        gaPreviewMinHeight={
          isGlobalAiChartPreview && isMapChart
            ? GLOBAL_AI_INLINE_CHART_PLOT_HEIGHT
            : undefined
        }
      >
        {!isMapChart && !hideHeader && (
          <ChartHeader hasActionMenu={showActionMenu}>
            <ChartHeaderTitleInfo
              title={chartTitle}
              {...(item?.info &&
                item?.id !== 'role-overview-summary' && {
                  info: item.info,
                })}
            />
            <div className={styles.headerSwitchers}>
              <ChartHeaderAction>
                <ChartSwitchers
                  primaryFilterId={primaryFilter?.id}
                  isSingleEntityChart={showPrimaryEntitySwitcher}
                  hasActionMenu={showActionMenu}
                />
                {showActionMenu && (
                  <ChartActionMenu
                    printRef={printRef}
                    item={item}
                    metric={metric}
                    title={chartTitle}
                    primaryFilterId={primaryFilter?.id}
                    slim={headerSize === 'sm'}
                    modalContent={chartBodyContent}
                  />
                )}
              </ChartHeaderAction>
            </div>
          </ChartHeader>
        )}
        <div
          ref={printRef}
          data-testid="card-body"
          className={classNames(
            styles.chartBody,
            !isMapChart && cardStyles.cardBody
          )}
          data-padding={cardBodyPadding}
          data-height={cardBodyHeight}
          data-ga-preview={isGlobalAiChartPreview || undefined}
          data-min-content={cardBodyMinContent || undefined}
          data-loader-contained={loaderContainedInChart || undefined}
          style={
            cardBodyHeight === 'plot'
              ? ({
                  '--plot-height': GLOBAL_AI_INLINE_CHART_PLOT_HEIGHT,
                } as CSSProperties)
              : undefined
          }
        >
          {chartBodyContent}
        </div>
      </ChartCard>
    </ChartHeaderBreadcrumbProvider>
  );
};

export const SpecialChartSelector = ({ data }: Pick<ChartProps, 'data'>) => {
  const { primaryFilter } = useTabContentConfig();
  const { config: pageConfig } = usePageConfig();
  const { item } = useChart();
  const metric = usePlotMetric();

  const primaryFilterId = primaryFilter?.id;

  const { chartType: hookChartType } = useAdaptiveChartType(item);
  const { chartType: baseChartType, secondaryDimension } = item?.plot ?? {};
  const chartType = hookChartType ?? baseChartType;

  if (!chartType || !item) {
    return <ChartNotSupported />;
  }

  /** ================================
   * Special Charts
   * ================================
   * These are charts with specific use cases and not generic */
  /** ======== Entity Overview Summary ========
   * Company Overview & Role Overview */
  if (chartType === 'entity-summary') {
    if (!primaryFilterId) return <ChartNotSupported />;
    return <EntitySummary primaryDimension={primaryFilterId} />;
  }
  /** ======== Mixed Stats ========
   * Company Overview & Role Overview */
  if (chartType === 'mixed-stats') {
    if (!primaryFilterId) return <ChartNotSupported />;
    return <MixedStatsChart primaryDimension={primaryFilterId} />;
  }
  if (chartType === 'icicle') {
    if (secondaryDimension === 'subsidiaries') {
      return <SubsidiariesIcicle />;
    }
    if (!primaryFilterId) return <ChartNotSupported />;
    return <IcicleChart primaryDimension={primaryFilterId} data={data} />;
  }
  if (chartType === 'subsidiary-snapshot') {
    return (
      <SubsidiarySnapshot
        kind={item.plot?.subsidiarySnapshot ?? 'concentration'}
      />
    );
  }
  if (chartType === 'subsidiary-trend') {
    return <SubsidiaryTrend trend={item.plot?.subsidiaryTrend ?? 'count'} />;
  }
  if (chartType === 'timeline') {
    return <SubsidiariesTimeline />;
  }
  /** ======== Share of Segment ======== */
  if (
    isShareOfSegmentPlot(item.plot) &&
    (metric === 'share_of_headcount' || metric === 'headcount') &&
    secondaryDimension &&
    isCompositionSecondaryDimension(secondaryDimension)
  ) {
    if (!primaryFilterId) return <ChartNotSupported />;
    return (
      <ShareOfSegmentChart
        primaryDimension={primaryFilterId}
        secondaryDimension={secondaryDimension}
        plotId={item.id}
        data={data}
      />
    );
  }
  /** ======== Dynamic / subfiltered Diverging Bar Combo ======== */
  if (isDynamicDivergingBarComboPlot(item.plot)) {
    if (!primaryFilterId) return <ChartNotSupported />;
    return (
      <DynamicDivergingBarCombo
        primaryDimension={primaryFilterId}
        plotId={item.id}
      />
    );
  }

  /** ======== Sentiment Review ======== */
  if (chartType === 'sentiment_review') {
    if (!primaryFilterId) return <ChartNotSupported />;
    return <SentimentReview primaryDimension={primaryFilterId} />;
  }

  /** ======== Generic Map ======== */
  if (chartType === 'map' && metric) {
    return (
      <MapChartSelector
        primaryDimension={primaryFilterId}
        metric={metric}
        mapOptions={item?.plot?.mapOptions ?? {}}
      />
    );
  }

  if (chartType === 'text') {
    return (
      <MultiMetricDataProvider
        item={item}
        chartType="text"
        primaryDimension={primaryFilterId}
        resolved={data}
      >
        {({ data, status, error }) => (
          <>
            {/* Always mount — besides the GA chart-preview overlay, this reporter
                feeds the custom-page NarrationCollector on real pages (no
                gaChartPreviewMeta). The component self-guards both effects, so
                mounting unconditionally is safe (matches TableChart). */}
            <GlobalAiChartPreviewDataReporter
              itemId={item.id}
              branch="multi"
              status={status}
              error={error}
              data={data}
              chartType={chartType}
              metric="text"
            />
            <TextChartCardContentWithFilters
              data={data}
              status={status}
              item={item}
              pageConfig={pageConfig}
            />
          </>
        )}
      </MultiMetricDataProvider>
    );
  }

  if (chartType === 'ai-summary') {
    return <AiSummaryChart />;
  }

  if (chartType === 'image') {
    return (
      <div className={styles.imageFrame}>
        <img
          src={item?.plot?.imageSrc}
          alt="Report visual"
          className={styles.image}
        />
      </div>
    );
  }

  return <ChartNotSupported />;
};
